import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Gutter Cleans",
  "Gutter Repairs",
  "Gutter Replacements",
  "Roof Cleans",
  "Roof Paints",
  "Roof Replacements",
  "Multiple Services / Other",
];

const SUBURBS = [
  "New Plymouth",
  "Bell Block",
  "Waitara",
  "Inglewood",
  "Stratford",
  "Hāwera",
  "Ōakura",
  "Ōpunake",
  "Other / Rural Taranaki",
];

const STEPS = [
  { num: "1", text: "Send the form — it lands straight in Jason's inbox." },
  { num: "2", text: "Jason calls you back to talk through the job." },
  { num: "3", text: "You get a straight, no-pressure quote." },
];

const EMPTY = { name: "", phone: "", suburb: "", service: "", details: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPick = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("trc:service", onPick);
    return () => window.removeEventListener("trc:service", onPick);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) {
      toast.error("Pick a service so Jason knows what you need.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request sent — Jason will be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong — please call 022 547 7534 instead.");
    } finally {
      setSending(false);
    }
  };

  const fieldCls =
    "border-white/10 bg-white/5 text-slate-100 placeholder:text-slate-500 focus-visible:ring-orange-500/60";

  return (
    <section id="quote" className="relative py-24 sm:py-32">
      <div
        className="pointer-events-none absolute bottom-0 left-[-10%] h-[28rem] w-[28rem] rounded-full bg-orange-500/10 blur-[140px]"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-orange-400">05 — Free quote</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            Tell us what's up
            <br />
            with your roof.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
            No call centres, no runaround — your request goes directly to the person who'll do the
            job.
          </p>
          <div className="mt-10 space-y-6">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex items-start gap-5"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center border border-orange-500/40 font-mono text-sm font-semibold text-orange-400">
                  {s.num}
                </span>
                <p className="pt-2 text-sm leading-relaxed text-slate-300">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.form
          data-testid="quote-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="border border-white/[0.08] bg-[#131924] p-6 sm:p-10 lg:col-span-7"
        >
          <div className="grid gap-6 sm:grid-cols-2">
            <div>
              <label htmlFor="quote-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Your name
              </label>
              <Input
                id="quote-name"
                data-testid="quote-input-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Sam Taylor"
                className={fieldCls}
              />
            </div>
            <div>
              <label htmlFor="quote-phone" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                Phone number
              </label>
              <Input
                id="quote-phone"
                data-testid="quote-input-phone"
                required
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="02x xxx xxxx"
                className={fieldCls}
              />
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="quote-suburb" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
              Location / suburb in Taranaki
            </label>
            <Input
              id="quote-suburb"
              data-testid="quote-select-suburb"
              required
              list="trc-suburbs"
              value={form.suburb}
              onChange={set("suburb")}
              placeholder="Start typing your suburb"
              className={fieldCls}
            />
            <datalist id="trc-suburbs">
              {SUBURBS.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>

          <div className="mt-6">
            <span className="mb-3 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
              Service needed
            </span>
            <div className="flex flex-wrap gap-2" data-testid="quote-radio-service">
              {SERVICES.map((s) => {
                const active = form.service === s;
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`quote-service-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
                    onClick={() => setForm((f) => ({ ...f, service: s }))}
                    className={`border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors duration-200 ${
                      active
                        ? "border-orange-500 bg-orange-500 text-[#0B0F17]"
                        : "border-white/15 text-slate-300 hover:border-orange-500/50 hover:text-orange-300"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            <label htmlFor="quote-details" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
              Job details
            </label>
            <Textarea
              id="quote-details"
              data-testid="quote-input-details"
              rows={4}
              value={form.details}
              onChange={set("details")}
              placeholder="Tell us a bit about the job — roof type, what you've noticed, access, anything urgent."
              className={fieldCls}
            />
          </div>

          <button
            type="submit"
            data-testid="quote-submit-button"
            disabled={sending}
            className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-orange-500 px-8 py-4 font-heading text-sm font-bold uppercase tracking-[0.15em] text-[#0B0F17] transition-colors duration-300 hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                Send quote request
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            Goes straight to Jason's inbox — usually same-day reply
          </p>
        </motion.form>
      </div>
    </section>
  );
}
